import {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type Ref,
} from 'react';
import type { Message } from '@chatapp-inbox/shared';
import { MessageItem } from './MessageItem';
import { PROVIDER_UNAVAILABLE_TEXT, STALE_PENDING_MS } from './message-status';
import type { LocalSend } from './outbox';
import type { MessageEntry, TimelineEntry } from './timeline';
import { messageElementId, useChatScroll } from './use-chat-scroll';
import { useTopSentinel } from './use-top-sentinel';
import styles from './MessageList.module.css';

/** Controles que a janela de chat usa por fora da lista. */
export interface MessageListHandle {
  /** Vai ao fim e passa a acompanhá-lo (a pessoa acabou de enviar uma mensagem). */
  stickToBottom: () => void;
  /** Foco na área de mensagens (rolável e focável por teclado). */
  focusLog: () => void;
  /** O foco está na área de mensagens agora? */
  logHasFocus: () => boolean;
  /** Foco numa mensagem (a de reenvio, por exemplo); false se ela não estiver na tela. */
  focusMessage: (key: string) => boolean;
}

export interface MessageListProps {
  ref?: Ref<MessageListHandle>;
  entries: readonly TimelineEntry[];
  /** Mensagens do SERVIDOR, em ordem cronológica. */
  messages: readonly Message[];
  viewerId: string;
  contactName: string;
  canWrite: boolean;
  /** Aviso "serviço de mensagens indisponível". */
  unavailable: boolean;
  hasOlder: boolean;
  loadingOlder: boolean;
  olderFailed: boolean;
  onLoadOlder: () => void;
  /** Foca a área de mensagens ao montar (a lista acabou de substituir uma tela de erro com foco). */
  focusOnMount: boolean;
  retrying: ReadonlySet<string>;
  retryErrors: Readonly<Record<string, string>>;
  /** Quando cada mensagem voltou a "enviando" por um reenvio (ms). */
  retriedAt: Readonly<Record<string, number>>;
  onRetry: (message: Message) => void;
  onRetryLocal: (local: LocalSend) => void;
  onEditLocal: (local: LocalSend) => void;
  onDiscardLocal: (local: LocalSend) => void;
}

const isWaiting = (local: LocalSend): boolean =>
  local.state === 'queued' || local.state === 'sending';

/** Desde quando a mensagem está "enviando" (ms): a tentativa atual, ou a criação no servidor. */
function pendingSince(entry: MessageEntry, retriedAt: Readonly<Record<string, number>>): number {
  if (entry.local) return entry.local.attemptedAt;
  return Math.max(Date.parse(entry.message.createdAt), retriedAt[entry.key] ?? 0);
}

/**
 * Relógio que só anda quando alguma mensagem "enviando" passa dos 30 s (sem polling): marca o
 * próximo prazo com um `setTimeout` e, ao disparar, relê a hora.
 */
function useStaleClock(
  entries: readonly TimelineEntry[],
  retriedAt: Readonly<Record<string, number>>,
): number {
  const [now, setNow] = useState(() => Date.now());
  // Só os prazos que AINDA não venceram: um que já disparou (`due <= now`) não pode continuar sendo
  // "o próximo", senão o relógio congela nele e as mensagens enviadas depois nunca viram
  // "Ainda enviando…". Ao disparar, `now` muda, o prazo é recalculado e o seguinte é agendado.
  const deadline = useMemo(() => {
    let earliest = Infinity;
    for (const entry of entries) {
      if (entry.kind !== 'message' || entry.message.direction !== 'out') continue;
      if (entry.local ? !isWaiting(entry.local) : entry.message.status !== 'pending') continue;
      const since = pendingSince(entry, retriedAt);
      if (Number.isNaN(since)) continue;
      const due = since + STALE_PENDING_MS + 1;
      if (due > now) earliest = Math.min(earliest, due);
    }
    return earliest;
  }, [entries, retriedAt, now]);

  useEffect(() => {
    if (deadline === Infinity) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [deadline]);

  return now;
}

function MessageListView({
  ref,
  entries,
  messages,
  viewerId,
  contactName,
  canWrite,
  unavailable,
  hasOlder,
  loadingOlder,
  olderFailed,
  onLoadOlder,
  focusOnMount,
  retrying,
  retryErrors,
  retriedAt,
  onRetry,
  onRetryLocal,
  onEditLocal,
  onDiscardLocal,
}: MessageListProps): ReactElement {
  const { scrollerRef, contentRef, onScroll, unseen, stickToBottom } = useChatScroll({
    messages,
    entries,
    viewerId,
  });
  const now = useStaleClock(entries, retriedAt);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  const setScrollerRef = useCallback(
    (element: HTMLDivElement | null) => {
      scrollerRef.current = element;
      setScroller(element);
    },
    [scrollerRef],
  );

  // Páginas antigas entram no meio de uma região "viva": sem isto o leitor de tela leria 50
  // mensagens de uma vez. Fica "desligada" durante a busca e um instante depois dela.
  const [settling, setSettling] = useState(false);
  const loadOlder = useCallback(() => {
    setSettling(true);
    onLoadOlder();
  }, [onLoadOlder]);
  useEffect(() => {
    if (!settling || loadingOlder) return;
    const timer = setTimeout(() => setSettling(false), 600);
    return () => clearTimeout(timer);
  }, [settling, loadingOlder]);
  const quiet = loadingOlder || settling;

  const sentinelRef = useTopSentinel({
    root: scroller,
    enabled: hasOlder && !loadingOlder && !olderFailed,
    onReach: loadOlder,
    loadedPages: messages.length,
  });

  useImperativeHandle(
    ref,
    () => ({
      stickToBottom,
      focusLog: () => scrollerRef.current?.focus({ preventScroll: true }),
      logHasFocus: () =>
        scrollerRef.current !== null && scrollerRef.current === document.activeElement,
      focusMessage: (key) => {
        const element = scrollerRef.current?.ownerDocument.getElementById(messageElementId(key));
        if (!element) return false;
        element.focus({ preventScroll: true });
        return true;
      },
    }),
    [stickToBottom, scrollerRef],
  );

  useEffect(() => {
    if (focusOnMount) scrollerRef.current?.focus({ preventScroll: true });
    // Só na montagem: depois disso a lista não rouba o foco.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se o elemento focado saiu da tela (o envio local virou a mensagem do servidor, por exemplo), o
  // navegador joga o foco no <body>. Devolve-o à área de mensagens.
  useLayoutEffect(() => {
    const element = lastFocused.current;
    if (!element || element.isConnected) return;
    lastFocused.current = null;
    const active = document.activeElement;
    if (!active || active === document.body) scrollerRef.current?.focus({ preventScroll: true });
  }, [entries, scrollerRef]);

  const empty = entries.length === 0;

  return (
    <div className={styles.area}>
      {/* A região existe antes do texto, para o leitor de tela anunciar quando o aviso aparecer. */}
      <div
        role="status"
        aria-label="Estado do serviço de mensagens"
        className={styles.bannerRegion}
      >
        {unavailable && <p className={styles.banner}>{PROVIDER_UNAVAILABLE_TEXT}</p>}
      </div>

      <div
        ref={setScrollerRef}
        role="log"
        aria-label="Mensagens"
        aria-live={quiet ? 'off' : 'polite'}
        aria-relevant="additions"
        // Área rolável: precisa ser alcançável pelo teclado para quem não usa mouse poder rolar.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={styles.scroller}
        onScroll={onScroll}
        onFocus={(event) => {
          lastFocused.current = event.target as HTMLElement;
        }}
        onBlur={(event) => {
          // Foco indo para outro elemento: não é remoção. Sem destino, pode ser remoção.
          if (event.relatedTarget) lastFocused.current = null;
        }}
      >
        <div ref={contentRef} className={styles.content}>
          <div className={styles.top} aria-live="off">
            {hasOlder ? (
              <>
                <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
                <button
                  type="button"
                  className={styles.older}
                  aria-disabled={loadingOlder || undefined}
                  data-loading={loadingOlder || undefined}
                  onClick={() => {
                    if (!loadingOlder) loadOlder();
                  }}
                >
                  {olderFailed ? 'Tentar de novo' : 'Carregar mensagens anteriores'}
                </button>
              </>
            ) : (
              !empty && <p className={styles.start}>Início da conversa</p>
            )}
            {/* A linha do erro tem lugar reservado enquanto há mais mensagens (`olderSlot`): se ela
                ocupasse espaço só ao aparecer, a conversa abaixo pularia na hora da falha e de "Tentar
                de novo". A região existe sempre, antes do texto, para o leitor de tela anunciá-lo. */}
            <p role="status" className={hasOlder ? styles.olderSlot : styles.olderStatus}>
              <span className={olderFailed ? styles.olderError : styles.olderStatus}>
                {loadingOlder
                  ? 'Carregando mensagens anteriores…'
                  : olderFailed
                    ? 'Não foi possível carregar as mensagens anteriores.'
                    : ''}
              </span>
            </p>
          </div>

          {empty && <p className={styles.empty}>Nenhuma mensagem nesta conversa ainda.</p>}

          <ul className={styles.list}>
            {entries.map((entry) =>
              entry.kind === 'day' ? (
                <li key={entry.key} className={styles.day}>
                  <span>{entry.label}</span>
                </li>
              ) : (
                <MessageItem
                  key={entry.key}
                  messageKey={entry.key}
                  message={entry.message}
                  local={entry.local}
                  contactName={contactName}
                  viewerId={viewerId}
                  canWrite={canWrite}
                  now={now}
                  since={pendingSince(entry, retriedAt)}
                  retrying={retrying.has(entry.key)}
                  retryError={retryErrors[entry.key] ?? null}
                  onRetry={onRetry}
                  onRetryLocal={onRetryLocal}
                  onEditLocal={onEditLocal}
                  onDiscardLocal={onDiscardLocal}
                />
              ),
            )}
          </ul>
        </div>
      </div>

      {unseen > 0 && (
        <button
          type="button"
          className={styles.pill}
          onClick={() => {
            stickToBottom();
            // O botão some ao ser usado: o foco vai antes para a área de mensagens.
            scrollerRef.current?.focus({ preventScroll: true });
          }}
        >
          Novas mensagens <span aria-hidden="true">↓</span>
        </button>
      )}
      <div role="status" aria-label="Aviso de novas mensagens" className="sr-only">
        {unseen === 1 ? 'Nova mensagem.' : unseen > 1 ? `${unseen} novas mensagens.` : ''}
      </div>
    </div>
  );
}

export const MessageList = memo(MessageListView);
